import { Component, inject, effect } from '@angular/core';
import { RagService } from '../services/rag.service';
import { KeywordExtractionService } from '../services/keyword-extraction.service';
import { FileTextService } from '../../../core/services/file-text.service';
import { RetrievalStrategy } from '../models/rag.models';
import { Router } from '@angular/router';

@Component({
  selector: 'app-rag-documents',
  templateUrl: './rag-documents.component.html',
  styleUrls: ['./rag-documents.component.scss'],
})
export class RagDocumentsComponent {
  protected readonly rag = inject(RagService);
  protected readonly keywordExtraction = inject(KeywordExtractionService);
  protected readonly strategies: RetrievalStrategy[] = ['bm25', 'vector'];
  protected readonly acceptedTypes = FileTextService.ACCEPT;
  protected isUploading = false;
  protected uploadError = '';
  protected keywords: string[] = [];
  private readonly fileText = inject(FileTextService);
  private readonly router = inject(Router);

  constructor() {
    // Initialize keywords using the keyword service
    this.keywords = this.keywordExtraction.getKeywordTermsFromDocuments(this.rag.documents(), 100);

    // Update keywords when documents change
    effect(() => {
      this.rag.documents();
      this.keywords = this.keywordExtraction.getKeywordTermsFromDocuments(this.rag.documents(), 100);
    });
  }

  protected async uploadFiles(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (!files.length) return;

    this.isUploading = true;
    this.uploadError = '';
    let failures = 0;
    try {
      for (const file of files) {
        const text = await this.fileText.extractText(file);
        if (text === null) {
          failures++;
          this.uploadError = `Skipped ${file.name} — only ${FileTextService.SUPPORTED_LABEL} are supported.`;
          continue;
        }
        await this.rag.addDocument(file.name, text, file.size);
      }
      if (failures === 0) this.uploadError = '';
    } catch (error) {
      console.error('Upload error:', error);
      this.uploadError = error instanceof Error ? error.message : 'Could not read the selected file.';
    } finally {
      this.isUploading = false;
      input.value = '';
    }
  }

  protected async chooseStrategy(strategy: RetrievalStrategy): Promise<void> {
    this.rag.strategy.set(strategy);
    await this.reindexDocuments();
  }

  protected async reindexDocuments(): Promise<void> {
    for (const document of this.rag.documents()) await this.rag.indexDocument(document.id);
  }

  protected formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  protected fileBadge(name: string): string {
    const lower = name.toLowerCase();
    const dot = lower.lastIndexOf('.');
    if (dot < 0) return 'FILE';
    const ext = lower.slice(dot + 1);
    if (ext.length <= 4) return ext.toUpperCase();
    return 'FILE';
  }

  private updateKeywords(): void {
    // This method is kept for backward compatibility but now delegates to the service.
    this.keywords = this.keywordExtraction.getKeywordTermsFromDocuments(this.rag.documents(), 100);
  }
  /**
   * Navigate to chat page with the selected keyword as query.
   */
  searchKeyword(keyword: string): void {
    this.router.navigate(['chat'], { queryParams: { q: keyword } });
  }}
